import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiGrid, 
  FiBookmark, 
  FiPackage, 
  FiDroplet, 
  FiPlus, 
  FiTrash2, 
  FiEdit3, 
  FiX,
  FiImage,
  FiCheck,
  FiMaximize2,
  FiMinimize2
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import Modal from '../../components/Modal';
import api, { getAuthToken } from '../../api/api';
import StatusConfirmationModal from '../components/StatusConfirmationModal';
import { DEFAULT_PRODUCT_IMAGE, getImageUrl } from '../../utils/constants';

// Add ImageViewer component
const ImageViewer = ({ isOpen, onClose, imageUrl, title }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
      onClick={onClose}
    >
      <div className="relative w-full h-full flex flex-col items-center justify-center p-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
        >
          <FiX className="w-6 h-6" />
        </button>
        <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
          <motion.img
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            src={imageUrl}
            alt={title}
            className="max-w-full max-h-full object-contain"
          />
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <h3 className="text-white text-lg font-medium">{title}</h3>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TaxonomyManagement = () => {
  const navigate = useNavigate();
  // Initialize all state variables
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandsError, setBrandsError] = useState(null);
  const [isBrandsLoading, setIsBrandsLoading] = useState(true);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  
  const [newItem, setNewItem] = useState('');
  const [newCategory, setNewCategory] = useState({ name: '', image: null });
  const [newBrand, setNewBrand] = useState({ name: '', image: null });
  
  const [isLoading, setIsLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [statusChangeItem, setStatusChangeItem] = useState(null);
  const [statusChangeType, setStatusChangeType] = useState(null); // 'category' or 'brand'

  // Add state to track failed images
  const [failedImages, setFailedImages] = useState(new Set());

  // Improved image error handler
  const handleImageError = useCallback((itemId) => {
    setFailedImages(prev => new Set(prev).add(itemId));
  }, []);

  // Image component with proper error handling
  const CategoryImage = ({ item }) => {
    if (failedImages.has(item.id) || !item.image) {
      return (
        <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded">
          <FiImage className="w-4 h-4 text-gray-400" />
        </div>
      );
    }

    return (
      <img 
        src={getImageUrl(item.image)}
        alt={item.name}
        className="w-8 h-8 object-cover rounded"
        onError={() => handleImageError(item.id)}
      />
    );
  };

  // Extract fetch categories logic into a reusable function
  const fetchCategories = async () => {
    try {
      const categoriesData = await api.getCategories();
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        toast.error('Session expired. Please login again');
      } else {
        toast.error('Failed to load categories');
      }
      return [];
    }
  };

  // Separate brands fetching logic
  const fetchBrands = async () => {
    setIsBrandsLoading(true);
    setBrandsError(null);
    try {
      const response = await api.getBrands();
      if (Array.isArray(response)) {
        setBrands(response);
      } else if (response && Array.isArray(response.results)) {
        // Handle paginated response if API returns { results: [] }
        setBrands(response.results);
      } else {
        console.error('Unexpected brands data format:', response);
        setBrands([]);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrandsError(error.message || 'Failed to fetch brands');
      toast.error('Failed to fetch brands. Please try again.');
      setBrands([]);
    } finally {
      setIsBrandsLoading(false);
    }
  };

  // Add fetchTags function
  const fetchTags = async () => {
    setIsTagsLoading(true);
    setTagsError(null);
    try {
      const response = await api.getTags();
      if (Array.isArray(response)) {
        setTags(response);
      } else {
        console.error('Unexpected tags data format:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTagsError(error.message || 'Failed to fetch tags');
      toast.error('Failed to fetch tags. Please try again.');
      setTags([]);
    } finally {
      setIsTagsLoading(false);
    }
  };

  // Main data fetching function
  const initializeTaxonomyData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchCategories(),
        fetchBrands(),
        fetchTags()
      ]);
    } catch (error) {
      console.error('Error initializing taxonomy data:', error);
      toast.error('Some data failed to load. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeTaxonomyData();
  }, []);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name?.trim()) {
      toast.error('Category name is required');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newCategory.name.trim());
      if (newCategory.image) {
        formData.append('image', newCategory.image);
      }

      const response = await api.createCategory(formData);
      await fetchCategories(); // Refresh the list to get the new image URL
      setNewCategory({ name: '', image: null });
      setShowCategoryModal(false);
      toast.success('Category added successfully');
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(error.message || 'Failed to add category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Image size should be less than 2MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      setNewBrand(prev => ({ ...prev, image: file }));
    }
  };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    if (!newBrand.name?.trim()) {
      toast.error('Brand name is required');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newBrand.name.trim());
      if (newBrand.image) {
        formData.append('image', newBrand.image);
      }

      await api.createBrand(formData);
      await fetchBrands(); // Refresh the brands list
      setNewBrand({ name: '', image: null });
      setShowBrandModal(false);
      toast.success('Brand added successfully');
    } catch (error) {
      console.error('Error adding brand:', error);
      toast.error(error.message || 'Failed to add brand');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a retry mechanism for brands
  const handleRetryBrands = () => {
    fetchBrands();
  };

  const tabs = [
    { id: 'categories', name: 'Categories', icon: FiGrid },
    { id: 'tags', name: 'Tags', icon: FiBookmark },
    { id: 'brands', name: 'Brands', icon: FiPackage }
  ];

  const getCurrentItems = () => {
    if (isLoading) return [];
    
    switch (activeTab) {
      case 'categories':
        // Filter active categories here
        return categories.filter(category => category.is_active) || [];
      case 'tags':
        if (isTagsLoading) return [];
        if (tagsError) return [];
        return tags || [];
      case 'brands':
        if (isBrandsLoading) return [];
        if (brandsError) return [];
        return brands || [];
      default:
        return [];
    }
  };

  const handleAdd = () => {
    if (newItem.trim()) {
      switch (activeTab) {
        case 'tags':
          setTags([...tags, newItem]);
          break;
        case 'brands':
          setBrands([...brands, newItem]);
          break;
      }
      setNewItem('');
    }
    setShowAddModal(false);
  };

  const handleDelete = (index) => {
    switch (activeTab) {
      case 'categories':
        setCategories(categories.filter((_, i) => i !== index));
        break;
      case 'tags':
        setTags(tags.filter((_, i) => i !== index));
        break;
      case 'brands':
        setBrands(brands.filter((_, i) => i !== index));
        break;
    }
  };

  // Add states for image viewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);

  // Add image viewer handler
  const handleImageClick = (imageUrl, title) => {
    setSelectedImageData({ url: imageUrl, title });
    setImageViewerOpen(true);
  };

  const renderList = (items) => {
    if (activeTab === 'brands' && isBrandsLoading) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (activeTab === 'brands' && brandsError) {
      return (
        <div className="text-center p-8">
          <p className="text-red-600 mb-4">{brandsError}</p>
          <button
            onClick={handleRetryBrands}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          >
            Retry Loading Brands
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {(activeTab === 'categories' || activeTab === 'brands') && (
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Image
                  </th>
                )}
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  {(activeTab === 'categories' || activeTab === 'brands') && (
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="relative w-12 h-12 group">
                        {item.image ? (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="relative w-12 h-12 cursor-pointer"
                            onClick={() => handleImageClick(getImageUrl(item.image), item.name)}
                          >
                            <img
                              src={getImageUrl(item.image)}
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DEFAULT_PRODUCT_IMAGE;
                              }}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200 flex items-center justify-center">
                              <FiMaximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </div>
                          </motion.div>
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FiImage className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {item.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {activeTab !== 'tags' && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="group relative inline-flex items-center justify-center p-2 rounded-xl bg-blue-50 hover:bg-blue-100 transition-all duration-200"
                          onClick={() => activeTab === 'categories' ? handleEditClick(item) : handleEditBrandClick(item)}
                        >
                          <FiEdit3 className="h-5 w-5 text-blue-600 transform transition-transform group-hover:rotate-12" />
                          <span className="sr-only">Edit {item.name}</span>
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="group relative inline-flex items-center justify-center p-2 rounded-xl bg-red-50 hover:bg-red-100 transition-all duration-200"
                        onClick={() => handleToggleClick(item, activeTab === 'tags' ? 'tag' : (activeTab === 'categories' ? 'category' : 'brand'))}
                      >
                        <FiTrash2 className="h-5 w-5 text-red-600" />
                        <span className="sr-only">Delete {item.name}</span>
                      </motion.button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleCategoryImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Image size should be less than 2MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      setNewCategory(prev => ({ ...prev, image: file }));
    }
  };

  const handleBrandLogoUpload = (file) => {
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Logo size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBrand(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddNewClick = () => {
    switch (activeTab) {
      case 'categories':
        setShowCategoryModal(true);
        break;
      case 'brands':
        setShowBrandModal(true);
        break;
      case 'tags':
        setShowTagModal(true);
        break;
      default:
        setShowAddModal(true);
    }
  };

  const getCategoryImagePreview = () => {
    if (!newCategory.image) return null;
    return typeof newCategory.image === 'string' 
      ? newCategory.image 
      : URL.createObjectURL(newCategory.image);
  };

  const handleEditCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory.name?.trim()) {
      toast.error('Category name is required');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', editingCategory.name.trim());
      if (editingCategory.image && editingCategory.image instanceof File) {
        formData.append('image', editingCategory.image);
      }

      await api.updateCategory(editingCategory.id, formData);
      await fetchCategories(); // Refresh the list
      setShowEditCategoryModal(false);
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error.message || 'Failed to update category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleClick = (item, type) => {
    setStatusChangeItem(item);
    setStatusChangeType(type);
  };

  const handleToggleActive = async () => {
    if (!statusChangeItem) return;

    try {
      await api.toggleCategoryActive(statusChangeItem.id);
      await fetchCategories(); // Refresh the list
      toast.success(`Category ${statusChangeItem.is_active ? 'deactivated' : 'activated'} successfully`);
      setStatusChangeItem(null); // Close modal
    } catch (error) {
      console.error('Error toggling category status:', error);
      toast.error(error.message || 'Failed to update category status. Please try again.');
    }
  };

  const handleEditClick = (category) => {
    setEditingCategory({
      id: category.id,
      name: category.name,
      image: category.image
    });
    setShowEditCategoryModal(true);
  };

  // Add this state for editing brands
  const [editingBrand, setEditingBrand] = useState({ id: null, name: '', image: null });
  const [showEditBrandModal, setShowEditBrandModal] = useState(false);

  // Add this handler function
  const handleEditBrand = async (e) => {
    e.preventDefault();
    if (!editingBrand.name?.trim()) {
      toast.error('Brand name is required');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', editingBrand.name.trim());
      if (editingBrand.image && editingBrand.image instanceof File) {
        formData.append('image', editingBrand.image);
      }

      await api.updateBrand(editingBrand.id, formData);
      await fetchBrands(); // Refresh the list
      setShowEditBrandModal(false);
      toast.success('Brand updated successfully');
    } catch (error) {
      console.error('Error updating brand:', error);
      toast.error(error.message || 'Failed to update brand');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBrandActive = async () => {
    if (!statusChangeItem) return;

    try {
      await api.toggleBrandActive(statusChangeItem.id);
      await fetchBrands(); // Refresh the list
      toast.success(`Brand ${statusChangeItem.is_active ? 'deactivated' : 'activated'} successfully`);
      setStatusChangeItem(null); // Close modal
    } catch (error) {
      console.error('Error toggling brand status:', error);
      toast.error(error.message || 'Failed to update brand status');
    }
  };

  // Add handleAddTag function
  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.name?.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setIsLoading(true);
    try {
      await api.createTag(newTag.name.trim());
      await fetchTags(); // Refresh the list
      setNewTag({ name: '' });
      setShowTagModal(false);
      toast.success('Tag added successfully');
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error(error.message || 'Failed to add tag');
    } finally {
      setIsLoading(false);
    }
  };

  // Add handleToggleTagActive function
  const handleToggleTagActive = async () => {
    if (!statusChangeItem) return;

    try {
      await api.toggleTagActive(statusChangeItem.id);
      await fetchTags(); // Refresh the list
      toast.success(`Tag ${statusChangeItem.is_active ? 'deactivated' : 'activated'} successfully`);
      setStatusChangeItem(null); // Close modal
    } catch (error) {
      console.error('Error toggling tag status:', error);
      toast.error(error.message || 'Failed to update tag status');
    }
  };

  // Add this state variable with the other state declarations (around line 22)
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTag, setNewTag] = useState({ name: '' });
  const [isTagsLoading, setIsTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState(null);

  // Add these state variables at the top with other state declarations
  const [showEditTagModal, setShowEditTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState({ id: null, name: '' });

  // Add handleEditTagClick function
  const handleEditTagClick = (tag) => {
    setEditingTag({
      id: tag.id,
      name: tag.name
    });
    setShowEditTagModal(true);
  };

  // Add handleEditTag function
  const handleEditTag = async (e) => {
    e.preventDefault();
    if (!editingTag.name?.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setIsLoading(true);
    try {
      await api.updateTag(editingTag.id, {
        name: editingTag.name.trim()
      });
      await fetchTags(); // Refresh the list
      setShowEditTagModal(false);
      toast.success('Tag updated successfully');
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error(error.message || 'Failed to update tag');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Taxonomy Management | Admin | Panchali Boutique</title>
      </Helmet>

      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Taxonomy Management</h1>
        </div>

        {/* Responsive Tab Navigation */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex px-4 sm:px-6 -mb-px min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex items-center gap-2 py-4 px-3 sm:px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200`}
              >
                <tab.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:block">{tab.name}</span>
                <span className="xs:hidden">{tab.name.charAt(0)}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="p-4 sm:p-6">
          {/* Show loading state */}
          {isLoading ? (
            <div className="p-4 text-center">
              <p>Loading...</p>
            </div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="space-y-4">
                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAddNewClick}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2"
                  >
                    <FiPlus className="h-5 w-5" />
                    Add New {activeTab.slice(0, -1)}
                  </motion.button>
                </div>
                {renderList(getCurrentItems())}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add New {activeTab.slice(0, -1)}</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={`Enter ${activeTab.slice(0, -1)} name`}
                />
                
                <div className="flex justify-end gap-3">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAdd}
                    className="px-4 py-2.5 text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all"
                  >
                    Add
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Category Modal */}
      <Modal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        title="Add New Category"
      >
        <form onSubmit={handleAddCategory} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Category Name *
            </label>
            <input
              type="text"
              required
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter category name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Category Image
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleCategoryImageUpload(e)}
                className="hidden"
                id="category-image-upload"
              />
              <label
                htmlFor="category-image-upload"
                className="cursor-pointer rounded-xl bg-indigo-50 px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-100"
              >
                Upload Image
              </label>
              {newCategory.image && (
                <span className="text-sm text-gray-500">
                  {typeof newCategory.image === 'string' 
                    ? 'Current image' 
                    : newCategory.image.name}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Recommended: 800x800px, Max 2MB</p>
            
            {/* Image Preview */}
            {newCategory.image && (
              <div className="mt-3">
                <div className="relative w-20 h-20">
                  <img
                    src={getCategoryImagePreview()}
                    alt="Category preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setNewCategory(prev => ({ ...prev, image: null }))}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCategoryModal(false)}
              className="rounded-xl px-4 py-2 text-gray-600 hover:bg-gray-100"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Adding...' : 'Add Category'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Brand Modal */}
      <Modal
        isOpen={showBrandModal}
        onClose={() => setShowBrandModal(false)}
        title="Add New Brand"
      >
        <form onSubmit={handleAddBrand} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Brand Name *
            </label>
            <input
              type="text"
              required
              value={newBrand.name}
              onChange={(e) => setNewBrand(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter brand name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Brand Image
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleBrandImageUpload}
                className="hidden"
                id="brand-image-upload"
              />
              <label
                htmlFor="brand-image-upload"
                className="cursor-pointer rounded-xl bg-indigo-50 px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-100"
              >
                Upload Image
              </label>
              {newBrand.image && (
                <span className="text-sm text-gray-500">
                  {newBrand.image.name}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Recommended: 400x400px, Max 2MB</p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowBrandModal(false)}
              className="rounded-xl px-4 py-2 text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Brand'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Category Modal */}
      <AnimatePresence>
        {showEditCategoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditCategoryModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative bg-blue-50 p-6">
                <button
                  onClick={() => setShowEditCategoryModal(false)}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="h-5 w-5" />
                </button>
                <h3 className="text-xl font-semibold text-gray-900">Edit Category</h3>
                <p className="mt-1 text-sm text-gray-500">Update category details below</p>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleEditCategory} className="p-6 space-y-6">
                {/* Category Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingCategory.name || ''}
                    onChange={(e) => setEditingCategory({
                      ...editingCategory,
                      name: e.target.value
                    })}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter category name"
                    required
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Image
                  </label>
                  <div className="mt-1 flex items-center gap-4">
                    {/* Image Preview */}
                    {(editingCategory.image instanceof File ? 
                      URL.createObjectURL(editingCategory.image) : 
                      editingCategory.image) ? (
                      <div className="relative h-20 w-20 rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={editingCategory.image instanceof File ? 
                            URL.createObjectURL(editingCategory.image) : 
                            editingCategory.image}
                          alt="Category preview"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setEditingCategory({
                            ...editingCategory,
                            image: null
                          })}
                          className="absolute top-1 right-1 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                        <FiImage className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <label className="flex-1">
                      <div className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                        <FiImage className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">Choose image</span>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setEditingCategory({
                              ...editingCategory,
                              image: file
                            });
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Recommended: 512x512px or larger, PNG or JPG
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowEditCategoryModal(false)}
                    className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiCheck className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Confirmation Modal */}
      <StatusConfirmationModal
        isOpen={!!statusChangeItem && statusChangeType === 'category'}
        onClose={() => {
          setStatusChangeItem(null);
          setStatusChangeType(null);
        }}
        onConfirm={handleToggleActive}
        itemName="Category"
        currentStatus={statusChangeItem?.is_active}
      />

      {/* Status Confirmation Modal for Brands */}
      <StatusConfirmationModal
        isOpen={!!statusChangeItem && statusChangeType === 'brand'}
        onClose={() => {
          setStatusChangeItem(null);
          setStatusChangeType(null);
        }}
        onConfirm={handleToggleBrandActive}
        itemName="Brand"
        currentStatus={statusChangeItem?.is_active}
      />

      {/* Edit Brand Modal */}
      <Modal
        isOpen={showEditBrandModal}
        onClose={() => setShowEditBrandModal(false)}
        title="Edit Brand"
      >
        <form onSubmit={handleEditBrand} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Brand Name *
            </label>
            <input
              type="text"
              required
              value={editingBrand.name}
              onChange={(e) => setEditingBrand(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter brand name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Brand Image
            </label>
            <div className="mt-1 flex items-center gap-4">
              {editingBrand.image && (
                <div className="relative h-20 w-20 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={editingBrand.image instanceof File ? 
                      URL.createObjectURL(editingBrand.image) : 
                      editingBrand.image}
                    alt="Brand preview"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setEditingBrand(prev => ({ ...prev, image: null }))}
                    className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl-lg"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              <label className="flex-1">
                <div className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <FiImage className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Choose image</span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setEditingBrand(prev => ({ ...prev, image: file }));
                    }
                  }}
                />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowEditBrandModal(false)}
              className="rounded-xl px-4 py-2 text-gray-600 hover:bg-gray-100"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Tag Modal */}
      <Modal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        title="Add New Tag"
      >
        <form onSubmit={handleAddTag} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tag Name *
            </label>
            <input
              type="text"
              required
              value={newTag.name}
              onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter tag name"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowTagModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-xl hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Adding...' : 'Add Tag'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Status Confirmation Modal for Tags */}
      <StatusConfirmationModal
        isOpen={!!statusChangeItem && statusChangeType === 'tag'}
        onClose={() => {
          setStatusChangeItem(null);
          setStatusChangeType(null);
        }}
        onConfirm={handleToggleTagActive}
        itemName="Tag"
        currentStatus={statusChangeItem?.is_active}
      />

      {/* Edit Tag Modal */}
      <Modal
        isOpen={showEditTagModal}
        onClose={() => setShowEditTagModal(false)}
        title="Edit Tag"
      >
        <form onSubmit={handleEditTag} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tag Name *
            </label>
            <input
              type="text"
              required
              value={editingTag.name}
              onChange={(e) => setEditingTag(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter tag name"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowEditTagModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-xl hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Add ImageViewer component */}
      <AnimatePresence>
        {imageViewerOpen && (
          <ImageViewer
            isOpen={imageViewerOpen}
            onClose={() => setImageViewerOpen(false)}
            imageUrl={selectedImageData?.url}
            title={selectedImageData?.title}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TaxonomyManagement;

// Add this to your tailwind.config.js
// In the theme.extend.screens section:
/*
screens: {
  'xs': '480px',
  // ... other breakpoints
}
*/



